/**
 * Query keys do domínio WhatsApp (CRM): config do canal por clube e o QR code
 * do pareamento do gateway (conexão por QR, aparelho vinculado).
 *
 * `configAll` invalida a config de TODOS os clubes por prefix-match — é o que
 * as mutations de conectar/desconectar usam, porque o diálogo pode estar
 * aberto para outro clube da rede. O QR fica FORA desse prefixo de propósito:
 * invalidar a config não deve disparar uma leitura extra do QR no meio do
 * pareamento (o polling dele tem ritmo próprio).
 *
 * `acoesDaIa` (Fase 2 do atendente de IA): as ações que a IA executou/propôs
 * numa conversa (`social_ai_actions`). `acoesDaIaAll` invalida todas — aprovar
 * pelo ✓ da sugestão mexe na conversa aberta, mas o cartão e a ficha leem a
 * mesma chave.
 */
import { tuple } from "./core";

const root = ["whatsapp"] as const;
const config = tuple(root, "config");
const acoesDaIa = tuple(root, "acoes-da-ia");
const creditosIa = tuple(root, "creditos-ia");

export const whatsapp = {
  all: root,
  /** Prefixo da config de todos os clubes (invalidação). */
  configAll: config,
  /** `club_whatsapp_config` de um clube. */
  config: (clubId: string | null | undefined) => tuple(config, clubId),
  /** QR code do pareamento por gateway de um clube (polling). */
  gatewayQr: (clubId: string | null | undefined) => tuple(root, "gateway-qr", clubId),
  /** `social_messages` de uma conversa (thread do inbox). */
  mensagens: (conversationId: string | null | undefined) => tuple(root, "mensagens", conversationId),
  /** Prefixo das ações da IA de todas as conversas (invalidação). */
  acoesDaIaAll: acoesDaIa,
  /** `social_ai_actions` de uma conversa. */
  acoesDaIa: (conversationId: string | null | undefined) => tuple(acoesDaIa, conversationId),
  /**
   * Quando a pré-reserva da IA vira cobrança (`ai_cobranca_pre_reserva`) no
   * clube da QUADRA — a linha própria do clube ou, sem ela, a da matriz. Fica
   * sob `configAll`: salvar a config relê.
   */
  cobrancaPreReserva: (courtId: string | null | undefined) => tuple(config, "cobranca-pre-reserva", courtId),
  /** Prefixo de tudo dos créditos de IA (invalidação depois de comprar/cancelar). */
  creditosIaAll: creditosIa,
  /** Saldo de créditos de IA do clube no ciclo (RPC `ia_saldo_do_clube`). */
  creditosIa: (clubId: string | null | undefined) => tuple(creditosIa, "saldo", clubId),
  /** Pacotes à venda, assinatura ativa e compras pendentes do clube. */
  creditosIaLoja: (clubId: string | null | undefined) => tuple(creditosIa, "loja", clubId),
  /** O usuário pode comprar créditos para ESTE clube? (dono/gerente — o mesmo teste da Edge). */
  creditosIaPermissao: (clubId: string | null | undefined) => tuple(creditosIa, "permissao", clubId),
  /** Admin da plataforma — tela "Créditos de IA" (prefixo para invalidar tudo dela). */
  creditosIaAdminAll: tuple(creditosIa, "admin"),
  creditosIaAdminRelatorio: (desde: string, ate: string) => tuple(creditosIa, "admin", "relatorio", desde, ate),
  creditosIaAdminLimites: tuple(creditosIa, "admin", "limites"),
  creditosIaAdminPacotes: tuple(creditosIa, "admin", "pacotes"),
  creditosIaAdminCompras: tuple(creditosIa, "admin", "compras"),
  /** Admin da plataforma — estado do WhatsApp por QR de todos os clubes (RPC `admin_whatsapp_qr_dos_clubes`). */
  qrGatewayAdmin: tuple(root, "admin", "qr-gateway"),
} as const;
